import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, Subject, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CompaniesService } from '../../../services/companies.service';
import {
  ITaggedNotesResponse,
  ITaggedNotesRequest,
  ITaggedNoteListItem,
  IUnreadCountResponse,
  INoteActivityResponse,
  INotesUpdateResponse,
  IConversationAttachment,
} from '../models/ITaggedNote.interface';
import { IMeetingNote, IInlineAttachment } from '../models/IMeetingNote.interface';

@Injectable({
  providedIn: 'root'
})
export class MeetingNotesPanelService {

  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompaniesService);
  private readonly baseUrl = environment.apiConfig.corporateAPI.endpoint;

  private get companyId(): number {
    return this.companyService.managedCompanyId;
  }

  // ============================================================
  // State Management
  // ============================================================

  private panelOpenSubject = new BehaviorSubject<boolean>(false);
  public panelOpen$ = this.panelOpenSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private selectedNoteSubject = new BehaviorSubject<ITaggedNoteListItem | null>(null);
  public selectedNote$ = this.selectedNoteSubject.asObservable();

  private taggedNotesSubject = new BehaviorSubject<ITaggedNoteListItem[]>([]);
  public taggedNotes$ = this.taggedNotesSubject.asObservable();

  private loadingListSubject = new BehaviorSubject<boolean>(false);
  public loadingList$ = this.loadingListSubject.asObservable();

  // Polling
  private readonly BADGE_POLL_INTERVAL = 30000;
  private readonly LIST_POLL_INTERVAL = 15000;
  private stopBadgePolling$ = new Subject<void>();
  private stopListPolling$ = new Subject<void>();
  private isBadgePolling = false;
  private isListPolling = false;
  private lastPollTimestamp: string | null = null;

  // ============================================================
  // Panel Controls
  // ============================================================

  togglePanel(): void {
    const isOpen = !this.panelOpenSubject.value;
    this.panelOpenSubject.next(isOpen);

    if (isOpen) {
      this.loadTaggedNotes();
      this.startListPolling();
    } else {
      this.stopListPolling();
      this.selectedNoteSubject.next(null);
    }
  }

  openPanel(): void {
    if (!this.panelOpenSubject.value) {
      this.panelOpenSubject.next(true);
      this.loadTaggedNotes();
      this.startListPolling();
    }
  }

  closePanel(): void {
    this.panelOpenSubject.next(false);
    this.stopListPolling();
    this.selectedNoteSubject.next(null);
  }

  selectNote(note: ITaggedNoteListItem): void {
    this.selectedNoteSubject.next(note);
    if (note.unreadCount > 0) {
      this.markAsRead(note.rootMeetingNoteId).subscribe(() => {
        const prevUnread = note.unreadCount;
        note.unreadCount = 0;
        const currentUnread = this.unreadCountSubject.value;
        this.unreadCountSubject.next(Math.max(0, currentUnread - prevUnread));
        const notes = this.taggedNotesSubject.value;
        const idx = notes.findIndex(n => n.noteId === note.noteId);
        if (idx >= 0) {
          notes[idx] = { ...note, unreadCount: 0 };
          this.taggedNotesSubject.next([...notes]);
        }
      });
    }
  }

  deselectNote(): void {
    this.selectedNoteSubject.next(null);
  }

  updateNoteInList(updatedNote: ITaggedNoteListItem): void {
    const notes = this.taggedNotesSubject.value;
    const idx = notes.findIndex(n => n.noteId === updatedNote.noteId);
    if (idx >= 0) {
      notes[idx] = { ...updatedNote };
      this.taggedNotesSubject.next([...notes]);
    }
  }

  // ============================================================
  // API Calls - Tagged Notes
  // ============================================================

  getTaggedNotes(request: ITaggedNotesRequest = {}): Observable<ITaggedNotesResponse> {
    let params = new HttpParams();
    if (request.page) params = params.set('page', request.page.toString());
    if (request.pageSize) params = params.set('pageSize', request.pageSize.toString());
    if (request.search) params = params.set('search', request.search);
    if (request.status && request.status !== 'all') params = params.set('status', request.status);
    if (request.departmentId) params = params.set('departmentId', request.departmentId.toString());
    if (request.sortBy) params = params.set('sortBy', request.sortBy);
    if (request.sortDir) params = params.set('sortDir', request.sortDir);

    const url = `${this.baseUrl}/api/meeting-notes/tagged`;
    console.log('[MeetingNotesPanel] GET tagged notes:', url, 'params:', request);
    return this.http.get<ITaggedNotesResponse>(url, { params }).pipe(
      tap(res => console.log('[MeetingNotesPanel] Tagged notes response:', res)),
    );
  }

  getUnreadCount(): Observable<IUnreadCountResponse> {
    return this.http.get<IUnreadCountResponse>(
      `${this.baseUrl}/api/meeting-notes/tagged/unread-count`
    );
  }

  markAsRead(rootNoteId: number): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/api/meeting-notes/${rootNoteId}/mark-read`, {}
    );
  }

  getActivity(rootNoteId: number, page: number = 1, pageSize: number = 50): Observable<INoteActivityResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<INoteActivityResponse>(
      `${this.baseUrl}/api/meeting-notes/${rootNoteId}/activity`, { params }
    );
  }

  getUpdatesSince(timestamp: string): Observable<INotesUpdateResponse> {
    const params = new HttpParams().set('timestamp', timestamp);
    return this.http.get<INotesUpdateResponse>(
      `${this.baseUrl}/api/meeting-notes/tagged/updates-since`, { params }
    );
  }

  // ============================================================
  // API Calls - Conversation Attachments
  // ============================================================

  attachDocument(formId: number, noteId: number, file: File, description?: string, client?: string): Observable<IConversationAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (client) formData.append('client', client);
    return this.http.post<IConversationAttachment>(
      `${this.baseUrl}/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/attachments`,
      formData
    );
  }

  getThreadAttachments(formId: number, rootNoteId: number): Observable<IConversationAttachment[]> {
    return this.http.get<IConversationAttachment[]>(
      `${this.baseUrl}/company/${this.companyId}/forms/${formId}/meeting-notes/${rootNoteId}/all-attachments`
    );
  }

  // ============================================================
  // API Calls - Inline Attachments
  // ============================================================

  uploadInlineAttachments(formId: number, noteId: number, files: File[]): Observable<IInlineAttachment[]> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return this.http.post<IInlineAttachment[]>(
      `${this.baseUrl}/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/inline-attachments`,
      formData
    );
  }

  deleteInlineAttachment(formId: number, noteId: number, attachmentId: number): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/inline-attachments/${attachmentId}`
    );
  }

  // ============================================================
  // Existing APIs Reused
  // ============================================================

  getNoteThread(formId: number, noteId: number): Observable<IMeetingNote> {
    return this.http.get<IMeetingNote>(
      `${this.baseUrl}/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}`
    );
  }

  joinThread(rootNoteId: number, formId: number): Observable<ITaggedNoteListItem> {
    return this.http.post<ITaggedNoteListItem>(
      `${this.baseUrl}/api/meeting-notes/${rootNoteId}/join?formId=${formId}`, {}
    );
  }

  // ============================================================
  // Polling Logic
  // ============================================================

  startBadgePolling(): void {
    if (this.isBadgePolling) return;
    this.isBadgePolling = true;

    this.refreshUnreadCount();

    interval(this.BADGE_POLL_INTERVAL).pipe(
      takeUntil(this.stopBadgePolling$),
      switchMap(() => this.getUnreadCount().pipe(
        catchError(() => of({ totalUnread: 0 }))
      ))
    ).subscribe((res) => {
      this.unreadCountSubject.next(res.totalUnread);
    });
  }

  stopBadgePolling(): void {
    this.stopBadgePolling$.next();
    this.isBadgePolling = false;
  }

  private startListPolling(): void {
    if (this.isListPolling) return;
    this.isListPolling = true;
    this.lastPollTimestamp = new Date().toISOString();

    interval(this.LIST_POLL_INTERVAL).pipe(
      takeUntil(this.stopListPolling$),
      switchMap(() => {
        if (this.lastPollTimestamp) {
          return this.getUpdatesSince(this.lastPollTimestamp).pipe(
            catchError(() => of({ updatedNotes: [], timestamp: this.lastPollTimestamp } as INotesUpdateResponse))
          );
        }
        return of({ updatedNotes: [], timestamp: new Date().toISOString() } as INotesUpdateResponse);
      })
    ).subscribe((updates) => {
      if (updates.updatedNotes.length > 0) {
        this.mergeUpdatedNotes(updates.updatedNotes);
      }
      this.lastPollTimestamp = updates.timestamp;
    });
  }

  private stopListPolling(): void {
    this.stopListPolling$.next();
    this.isListPolling = false;
  }

  // ============================================================
  // Data Loading & Merging
  // ============================================================

  loadTaggedNotes(request: ITaggedNotesRequest = {}): void {
    this.loadingListSubject.next(true);

    const defaultRequest: ITaggedNotesRequest = {
      page: 1,
      pageSize: 50,
      sortBy: 'lastActivityDate',
      sortDir: 'desc',
      ...request
    };

    this.getTaggedNotes(defaultRequest).pipe(
      catchError((err) => {
        console.error('[MeetingNotesPanel] Error loading tagged notes:', err);
        console.error('[MeetingNotesPanel] Error status:', err?.status, 'message:', err?.message);
        return of({ items: [], totalCount: 0, totalUnread: 0, pageNumber: 1, pageSize: 50 } as ITaggedNotesResponse);
      })
    ).subscribe((response) => {
      console.log('[MeetingNotesPanel] Setting notes:', response.items?.length, 'items, unread:', response.totalUnread);
      this.taggedNotesSubject.next(response.items);
      this.unreadCountSubject.next(response.totalUnread);
      this.loadingListSubject.next(false);
    });
  }

  refreshUnreadCount(): void {
    this.getUnreadCount().pipe(
      catchError(() => of({ totalUnread: 0 }))
    ).subscribe((res) => {
      this.unreadCountSubject.next(res.totalUnread);
    });
  }

  private mergeUpdatedNotes(updatedNotes: ITaggedNoteListItem[]): void {
    const currentNotes = [...this.taggedNotesSubject.value];
    let totalUnreadDelta = 0;

    for (const updated of updatedNotes) {
      const existingIdx = currentNotes.findIndex(n => n.rootMeetingNoteId === updated.rootMeetingNoteId);
      if (existingIdx >= 0) {
        totalUnreadDelta += (updated.unreadCount - currentNotes[existingIdx].unreadCount);
        currentNotes[existingIdx] = updated;
      } else {
        currentNotes.unshift(updated);
        totalUnreadDelta += updated.unreadCount;
      }
    }

    currentNotes.sort((a, b) =>
      new Date(b.lastActivityDate).getTime() - new Date(a.lastActivityDate).getTime()
    );

    this.taggedNotesSubject.next(currentNotes);

    if (totalUnreadDelta !== 0) {
      const newTotal = Math.max(0, this.unreadCountSubject.value + totalUnreadDelta);
      this.unreadCountSubject.next(newTotal);
    }
  }

  // ============================================================
  // Utility
  // ============================================================

  get isPanelOpen(): boolean {
    return this.panelOpenSubject.value;
  }

  get currentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  get currentSelectedNote(): ITaggedNoteListItem | null {
    return this.selectedNoteSubject.value;
  }
}
